/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'carbon-addons-iot-react';
import { useField } from 'formik';
import noop from 'lodash/noop';

import { TextValueFormik, FormLabelTooltip } from 'Common';

const propTypes = {
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  itemToString: PropTypes.func,
  onChange: PropTypes.func,

  /**
   * When true, if form input is disabled it will render as if it were
   * just a normal (potentially labelled) text value and not as a disabled input.
   * This pattern (input when enabled, plaintext when disabled) is used repeatedly
   * throughout the connect design.
   */
  renderAsTextWhenDisabled: PropTypes.bool,
  tooltip: PropTypes.string,
  /** goes into the dropdown itself */
  label: PropTypes.string,
  /** goes above the dropdown if specified */
  titleText: PropTypes.string,
  required: PropTypes.bool,
  learnMore: PropTypes.string,
};

const defaultProps = {
  disabled: false,
  itemToString: undefined,
  tooltip: undefined,
  // set empty label by default
  label: '',
  titleText: undefined,
  required: false,
  learnMore: undefined,
  onChange: noop,
  renderAsTextWhenDisabled: true,
};

const DropdownFormik = ({
  id,
  disabled,
  renderAsTextWhenDisabled,
  items,
  itemToString,
  onChange,
  learnMore,
  tooltip,
  required,
  titleText,
  ...other
}) => {
  const [field, meta] = useField(id);
  const labelComponent = tooltip ? (
    <FormLabelTooltip
      label={titleText || id}
      required={required}
      tooltip={tooltip}
      learnMore={learnMore}
    />
  ) : (
    titleText || id
  );
  return renderAsTextWhenDisabled && disabled ? (
    <TextValueFormik
      renderDataFunction={itemToString}
      id={id}
      hideLabel={!labelComponent}
      label={labelComponent}
    />
  ) : (
    <Dropdown
      id={id}
      items={items}
      itemToString={itemToString}
      onChange={e => {
        // and pass up the event in case the container components needs to react to this event in some way
        onChange(e);

        field.onChange({
          type: 'change',
          target: { value: e.selectedItem, name: id },
        });
      }}
      onBlur={e => setTimeout(() => field.onBlur(e), 200)}
      invalid={meta.error && meta.touched}
      invalidText={meta.error}
      disabled={disabled}
      initialSelectedItem={field.value}
      selectedItem={field.value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
      titleText={labelComponent}
    />
  );
};

DropdownFormik.propTypes = propTypes;
DropdownFormik.defaultProps = defaultProps;

export default DropdownFormik;
